import React from 'react';
import {
  ExternalIntegrationType,
  useTrackIntegrationHandoffMutation,
} from 'apollo/generated/client-operations';

type HandoffOptions = {
  /**
   * Whether the handoff was triggered by a user gesture and should open Avigilon in a
   * new tab. When `true` we open (and keep alive) a tab synchronously — this runs before
   * the first `await`, i.e. still inside the click turn — so popup blockers attribute it
   * to the tap. When `false` (a gesture-less `/cameras` visit) we redirect the current
   * tab instead. See sc-17102.
   */
  openInNewTab: boolean;
};

// Minimal same-origin placeholder written into the blank tab. iOS Safari discards an
// untouched `about:blank` browsing context if the click turn ends without writing to it,
// which would strand the handoff; a placeholder keeps it alive during the mutation.
const PLACEHOLDER_HTML =
  '<!doctype html><meta charset="utf-8"><title>Opening cameras…</title>' +
  '<body style="margin:2rem;font:16px system-ui,-apple-system,sans-serif">Opening cameras…</body>';

/**
 * Returns a function that fetches the Avigilon handoff url and sends the user there.
 *
 * The url is only known after an async mutation, so a deferred `window.open(url,
 * '_blank')` is killed by popup blockers (iOS Safari always, desktop Safari
 * intermittently) — the popup is no longer attributed to a user gesture. For gesture
 * callers we open a blank tab synchronously and only point it at the url once it
 * resolves. The returned promise rejects on failure (after cleaning up the tab) so
 * callers can surface it; it never leaves an unhandled rejection when awaited/caught.
 */
export function useAvigilonCamerasHandoff(): (
  options: HandoffOptions,
) => Promise<void> {
  const [trackIntegrationHandoffMutation] = useTrackIntegrationHandoffMutation({
    fetchPolicy: 'no-cache',
    variables: { integrationType: ExternalIntegrationType.Avigilon },
  });

  return React.useCallback(
    async ({ openInNewTab }: HandoffOptions): Promise<void> => {
      // Opened synchronously (before the first await) so it stays within the gesture.
      // `null` means the browser blocked the popup at open time.
      let newTab: Window | null = null;
      if (openInNewTab) {
        newTab = window.open('', '_blank');
        if (newTab) {
          newTab.document.write(PLACEHOLDER_HTML);
          newTab.document.close();
        }
      }

      let url: string | undefined;
      try {
        const result = await trackIntegrationHandoffMutation();
        url = result.data?.trackIntegrationHandoff?.url ?? undefined;
      } catch (error) {
        if (newTab && !newTab.closed) {
          newTab.close();
        }
        throw error;
      }

      if (!url) {
        if (newTab && !newTab.closed) {
          newTab.close();
        }
        throw new Error('Avigilon cameras handoff returned no url');
      }

      if (!openInNewTab) {
        // Gesture-less direct `/cameras` visit: replace so Back skips the interstitial.
        window.location.replace(url);
        return;
      }

      if (newTab === null) {
        // Popup was blocked at open time: navigate the current tab instead of failing.
        window.location.href = url;
        return;
      }

      if (newTab.closed) {
        // The user closed the placeholder tab while the mutation was in flight. Abort
        // rather than yanking the portal tab away (that would be the original bug).
        return;
      }

      // Sever the opener link before handing the still-same-origin tab to the external
      // Avigilon page (reverse-tabnabbing protection). `noopener` on `window.open` would
      // have returned `null` and dropped the handle this fix depends on, so clear it here.
      try {
        newTab.opener = null;
      } catch {
        // `opener` is read-only in a few browsers; never let that block the navigation.
      }
      newTab.location.href = url;
    },
    [trackIntegrationHandoffMutation],
  );
}
